<template>
  <div class="tw-flex tw-items-center tw-gap-4">
    <SfBannerSystem
      variant="neutral"
      :show-close="false"
      class="tw-rounded-lg tw-overflow-hidden tw-px-2"
    >
      {{ I18n.t('account_mapping_list.old_screen_retired_soon') }}
      <template #actions>
        <a :href="manualMappingPath" class="sf-btn sf-btn-small sf-btn-neutral">
          {{ I18n.t('account_mapping_list.previous_screen') }}
        </a>
      </template>
    </SfBannerSystem>
    <a class="sf-btn" :href="configurationCompanyPath">{{
      I18n.t('company.edit_company')
    }}</a>
    <a
      class="sf-btn sf-btn-success"
      :class="{
        'sf-disabled': saving || !getHasUnsavedChanges,
        requested: asyncRequested,
      }"
      :data-track="isInAiMode"
      @click="handleSaveClick"
    >
      <div v-if="saving" class="sf-spinner sf-inline-block" />
      {{ I18n.t('common.save_changes') }}
    </a>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia'

import { getSafeJSONValue, setJSONValue } from '@app/utils/local-storage'
import { ON_LOAD_BANNERS_KEY } from '@app/application/layout/sections/sf-flash-manager/constants'
import { useBannerManager } from '@app/shared/pinia/banner-manager'
import { bannerVariants } from '@app/shared/types/banner'
import { useCommon } from '@app/application/account-mapping/store/common'
import { useLocale } from '@app/application/account-mapping/store/locale'
import SfBannerSystem from '@app/shared/vue/components/sf-banner/sf-banner-system.vue'

export default {
  components: {
    SfBannerSystem,
  },

  props: {
    authenticityToken: {
      type: String,
      required: true,
    },
    saveMethod: {
      type: String,
      required: true,
    },
    asyncSaveUrl: {
      type: String,
      required: true,
    },
    saveUrl: {
      type: String,
      required: true,
    },
    saveBatchSize: {
      type: Number,
      required: true,
    },
    aiMode: {
      type: Boolean,
    },
  },
  data() {
    return {
      saving: false,
      asyncRequested: false,
    }
  },
  computed: {
    ...mapState(useCommon, ['getHasUnsavedChanges']),
    ...mapState(useLocale, ['supportedLocales']),
    configurationCompanyPath() {
      return Silverfin.Routes.configuration_company_path(
        Silverfin.currentFirmId,
        Silverfin.currentCompanyId
      )
    },
    manualMappingPath() {
      return Silverfin.Routes.edit_mapping_accounts_path(
        Silverfin.currentFirmId,
        Silverfin.currentCompanyId
      )
    },
    isInAiMode() {
      return this.aiMode ? 'ai-mapping-save' : ''
    },
  },
  mounted() {
    const banners = getSafeJSONValue(ON_LOAD_BANNERS_KEY)
    if (banners?.length) {
      this.raiseBanner({
        id: `account-mapping-save-banner`,
        variant: bannerVariants.danger,
        description: banners,
        title: I18n.t('account_mapping_list.save_error_title'),
        showClose: false,
      })

      setJSONValue(ON_LOAD_BANNERS_KEY, null)
    }
  },
  beforeUnmount() {
    this.unsubscribeMessageBus()
  },

  methods: {
    ...mapActions(useCommon, ['saveMappingAsync', 'saveMappingBatched']),
    ...mapActions(useBannerManager, ['raiseBanner']),

    waitForAsync(messageBusPayload) {
      if (messageBusPayload.status === 'done') {
        setJSONValue(
          ON_LOAD_BANNERS_KEY,
          messageBusPayload.accounts_with_errors
        )
      } else {
        setJSONValue(ON_LOAD_BANNERS_KEY, messageBusPayload.message)
      }
      this.unsubscribeMessageBus()
      Turbolinks.visit(messageBusPayload.next_path, { action: 'replace' })
      this.saving = false
      this.asyncRequested = false
    },

    handleSaveClick: async function () {
      if (!this.getHasUnsavedChanges) {
        return
      }

      this.saving = true

      if (Silverfin.currentFirmFeatures.supports_table_mapping_screen_async) {
        this.asyncSaving()
      } else {
        this.batchSaving()
      }
    },

    asyncSaving: async function () {
      const params = {
        url: this.asyncSaveUrl,
        authenticityToken: this.authenticityToken,
        supportedLocales: this.supportedLocales,
      }

      try {
        const response = await this.saveMappingAsync(params)
        const { messageBusChannelName } = response

        this.asyncRequested = true
        this._messageBusChannelName = messageBusChannelName

        window.Silverfin.Polling.subscribe(
          messageBusChannelName,
          this.waitForAsync,
          0
        )
      } catch (error) {
        console.error('Error starting async save:', error)
        this.saving = false

        const errorBody = error?.body
        this.raiseBanner({
          id: `account-mapping-async-save-error`,
          variant: bannerVariants.danger,
          title:
            errorBody?.message ||
            I18n.t('account_mapping_list.save_error_title'),
          showClose: true,
        })
      }
    },

    unsubscribeMessageBus: function () {
      if (this._messageBusChannelName) {
        window.Silverfin.Polling.unsubscribe(
          this._messageBusChannelName,
          this.waitForAsync
        )
        this._messageBusChannelName = null
      }
    },

    batchSaving: async function () {
      const params = {
        url: this.saveUrl,
        method: this.saveMethod,
        authenticityToken: this.authenticityToken,
        saveBatchSize: this.saveBatchSize,
        supportedLocales: this.supportedLocales,
      }

      try {
        const response = await this.saveMappingBatched(params)
        const { errors, nextPath } = response
        setJSONValue(ON_LOAD_BANNERS_KEY, errors)

        if (nextPath) {
          Turbolinks.visit(nextPath, { action: 'replace' })
        }
      } catch (error) {
        const errorBody = error?.body
        if (errorBody?.errorType === 'concurrency') {
          this.raiseBanner({
            id: 'account-mapping-concurrency-error',
            variant: bannerVariants.danger,
            title: errorBody.message,
            showClose: true,
          })
        } else {
          this.raiseBanner({
            id: 'account-mapping-save-error',
            variant: bannerVariants.danger,
            title: I18n.t('account_mapping_list.save_error_title'),
            showClose: true,
          })
        }
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
